import styled from 'styled-components';

import DownloadSvg from '../assets/icons/DownloadSvg';
import ExternalLinkSvg from '../assets/icons/ExternalLinkSvg';
import ToolkitSvg from '../assets/icons/ToolkitSvg';
import theme from '../assets/theme';

// Resource links (from the Training & PD resource doc). Keep these in one place
// so they are easy to swap when Rebecca finalises the remaining resources.
const COURSE_URL =
  'https://share.minicoursegenerator.com/creating-teaching-and-reflecting-with-curipod-638750671135332681';
const QUICK_START_VIDEO_ID = 'HoYeoQhnp-w';
const QUICK_START_VIDEO_URL = `https://www.youtube.com/embed/${QUICK_START_VIDEO_ID}`;
const ELA_VIDEO_ID = 'qB7_KP2Fg6Q';
const ELA_VIDEO_URL = `https://www.youtube.com/embed/${ELA_VIDEO_ID}`;
const SCIENCE_VIDEO_ID = 'T_1ZnKiIJc4';
const SCIENCE_VIDEO_URL = `https://www.youtube.com/embed/${SCIENCE_VIDEO_ID}`;
const DEMO_PD_KIT_URL =
  'https://drive.google.com/file/d/1sM04QksrnPDfX7aoGVl_uGfWeJ6E13z0/view?usp=sharing';
const WRITING_GUIDE_URL =
  'https://drive.google.com/file/d/1StJRdADoQepNEM2DvyTauUqdYFU-y17y/view?usp=sharing';
const HELP_CENTER_URL = 'https://help.curipod.com/en/';

const TrainingAndPDPage = () => {
  return (
    <Container>
      {/* ---------------------------------------------------------------- Hero */}
      <HeroSection>
        <HeroEyebrow>For teachers, coaches &amp; admins</HeroEyebrow>
        <HeroTitle>Training &amp; Professional Development</HeroTitle>
        <HeroLead>
          Learn Curipod at your own pace, then bring it to your team. Everything here is
          built to help you get started fast and share it with colleagues.
        </HeroLead>
        <HeroActions>
          <PrimaryCta href={COURSE_URL} target="_blank" rel="noopener noreferrer">
            Start with the course
          </PrimaryCta>
          <GhostCta href="#by-subject">Browse by subject</GhostCta>
        </HeroActions>
      </HeroSection>

      {/* --------------------------------------------------------- Start here */}
      <Section $background={theme.colors.white}>
        <SectionHeader>
          <SectionEyebrow>
            <StepBadge>1</StepBadge>
            Start here
          </SectionEyebrow>
          <SectionTitle>Get up and running</SectionTitle>
          <SectionLead>
            New to Curipod? Begin with the self-paced course, then keep the quick-start
            video handy for your team.
          </SectionLead>
        </SectionHeader>

        <CardGrid>
          <Card>
            <CardEyebrow>Self-paced course</CardEyebrow>
            <CardTitle>Getting Started Course</CardTitle>
            <CardBody>
              Create, teach, and reflect with Curipod — a short, self-paced course that
              walks you through your first lesson from start to finish.
            </CardBody>
            <CardActions>
              <PrimaryCta href={COURSE_URL} target="_blank" rel="noopener noreferrer">
                Start the course →
              </PrimaryCta>
            </CardActions>
          </Card>

          <Card>
            <CardEyebrow>5-minute video</CardEyebrow>
            <CardTitle>Quick Start Video</CardTitle>
            <CardBody>
              A five-minute overview for when you&apos;d rather watch than read — quick to
              share with the whole team.
            </CardBody>
            <VideoFrame>
              <iframe
                src={QUICK_START_VIDEO_URL}
                title="Curipod Quick Start Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </VideoFrame>
          </Card>
        </CardGrid>
      </Section>

      {/* --------------------------------------------------- Curipod by subject */}
      <Section id="by-subject" $background={theme.colors.white}>
        <SectionHeader>
          <SectionEyebrow>
            <StepBadge>2</StepBadge>
            Curipod by subject
          </SectionEyebrow>
          <SectionTitle>See it in your subject</SectionTitle>
          <SectionLead>
            See what Curipod looks like in your subject — a short walkthrough for each.
          </SectionLead>
        </SectionHeader>

        <CardGrid>
          <Card>
            <AccentBar $color={theme.colors.blue} />
            <CardTitle>ELA in Curipod</CardTitle>
            <CardBody>
              Reading, writing and discussion that gets every student responding.
            </CardBody>
            <VideoFrame>
              <iframe
                src={ELA_VIDEO_URL}
                title="ELA in Curipod"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </VideoFrame>
          </Card>

          <Card>
            <AccentBar $color={theme.colors.green} />
            <CardTitle>Science &amp; Social Studies in Curipod</CardTitle>
            <CardBody>
              Claims, evidence and reasoning — plus discussion that surfaces every voice.
            </CardBody>
            <VideoFrame>
              <iframe
                src={SCIENCE_VIDEO_URL}
                title="Science & Social Studies in Curipod"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </VideoFrame>
          </Card>
        </CardGrid>
      </Section>

      {/* --------------------------------------------------- Guides & downloads */}
      <Section $background={theme.colors.white}>
        <SectionHeader>
          <SectionEyebrow>
            <StepBadge>3</StepBadge>
            Guides &amp; downloads
          </SectionEyebrow>
          <SectionTitle>Resources to plan and lead PD</SectionTitle>
          <SectionLead>
            Print-ready resources for planning lessons and leading PD with your team.
          </SectionLead>
        </SectionHeader>

        <CardGrid>
          <Card>
            <CardIcon $tint={theme.colors.lightOrangeBg} $color={theme.colors.orangeDark}>
              <ToolkitSvg />
            </CardIcon>
            <CardEyebrow>Ready to run</CardEyebrow>
            <CardTitle>Demo &amp; PD Kits</CardTitle>
            <CardBody>
              Everything you need to run a 20–40 minute demo or a full 60-minute PD
              session with colleagues.
            </CardBody>
            <CardActions>
              <PrimaryCta
                href={DEMO_PD_KIT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open the kit
              </PrimaryCta>
            </CardActions>
          </Card>

          <Card>
            <CardIcon $tint={theme.colors.lightestGreen} $color={theme.colors.green}>
              <DownloadSvg />
            </CardIcon>
            <CardEyebrow>Cross-curricular</CardEyebrow>
            <CardTitle>Writing Across the Curriculum</CardTitle>
            <CardBody>
              A guide to building short, high-impact writing into any subject.
            </CardBody>
            <CardActions>
              <GhostCta
                href={WRITING_GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <DownloadSvg />
                Download
              </GhostCta>
            </CardActions>
          </Card>
        </CardGrid>
      </Section>

      {/* --------------------------------------------------------- Need a hand */}
      <HelpSectionOuter>
        <HelpBand>
          <HelpContent>
            <HelpTitle>Need a hand?</HelpTitle>
            <HelpText>
              Our Help Center has step-by-step articles for every feature — plus a
              friendly team when you get stuck.
            </HelpText>
          </HelpContent>
          <PrimaryCta href={HELP_CENTER_URL} target="_blank" rel="noopener noreferrer">
            <ExternalLinkSvg />
            Visit the Help Center →
          </PrimaryCta>
        </HelpBand>
      </HelpSectionOuter>
    </Container>
  );
};

export default TrainingAndPDPage;

const MOBILE_QUERY = '(max-width: 900px)';

const Container = styled.div`
  width: 100%;
  background: ${theme.colors.landingPageBeige};
  color: ${theme.colors.black};
`;

/* Button-styled links. Like the other marketing pages (safe-ai-in-curipod,
   school-and-free-plan), CTAs that navigate are anchors styled to match the
   shared Button rather than the Button component itself — so they stay valid
   single <a> elements. Styling mirrors the Button "orange" and "ghost" types. */
const CtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 5px;
  font-family: 'Nunito';
  font-size: 16px;
  font-weight: bold;
  line-height: 22px;
  text-decoration: none;
  transition: 0.4s;

  svg {
    height: 16px;
    width: 16px;
  }
`;

const PrimaryCta = styled(CtaLink)`
  padding: 11.5px 25px;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};

  &:hover,
  &:focus,
  &:active {
    background: ${theme.colors.orangeDark};
    color: ${theme.colors.white};
  }
`;

const GhostCta = styled(CtaLink)`
  padding: 10.5px 24px;
  background: transparent;
  border: 1px solid ${theme.colors.black};
  color: ${theme.colors.black};

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: ${theme.colors.black};
  }

  &:focus,
  &:active {
    color: ${theme.colors.black};
  }
`;

/* ------------------------------------------------------------------- Hero */
const HeroSection = styled.section`
  max-width: 1128px;
  margin: 0 auto;
  padding: 96px 24px 72px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;

  @media ${MOBILE_QUERY} {
    padding: 72px 20px 56px;
  }
`;

const HeroEyebrow = styled.p`
  margin: 0 0 18px;
  color: ${theme.colors.green};
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 2.6px;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  margin: 0;
  max-width: 820px;
  font-size: clamp(40px, 6vw, 68px);
  line-height: 1.02;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const HeroLead = styled.p`
  margin: 26px 0 0;
  max-width: 680px;
  font-size: 21px;
  line-height: 1.5;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.78);

  @media ${MOBILE_QUERY} {
    font-size: 18px;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 34px;
`;

/* ---------------------------------------------------------------- Sections */
const Section = styled.section<{ $background: string }>`
  padding: 88px 24px;
  background: ${({ $background }) => $background};

  @media ${MOBILE_QUERY} {
    padding: 64px 20px;
  }
`;

const SectionHeader = styled.div`
  max-width: 1128px;
  margin: 0 auto 40px;
`;

const SectionEyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 14px;
  color: ${theme.colors.green};
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 2.4px;
  text-transform: uppercase;
`;

const StepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: 900;
  letter-spacing: 0;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(30px, 4.5vw, 46px);
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: -0.03em;
`;

const SectionLead = styled.p`
  margin: 16px 0 0;
  max-width: 720px;
  font-size: 19px;
  line-height: 1.55;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.72);

  @media ${MOBILE_QUERY} {
    font-size: 17px;
  }
`;

/* ------------------------------------------------------------------- Cards */
const CardGrid = styled.div`
  max-width: 1128px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;

  @media ${MOBILE_QUERY} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  padding: 30px;
  border-radius: 20px;
  background: ${theme.colors.landingPageBeige};
  border: 1px solid rgba(40, 47, 51, 0.08);
  box-shadow: 0 18px 40px rgba(40, 47, 51, 0.06);
`;

const AccentBar = styled.div<{ $color: string }>`
  width: 56px;
  height: 6px;
  border-radius: 999px;
  margin-bottom: 18px;
  background: ${({ $color }) => $color};
`;

const CardIcon = styled.div<{ $tint: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 18px;
  border-radius: 12px;
  background: ${({ $tint }) => $tint};
  color: ${({ $color }) => $color};

  svg {
    width: 22px;
    height: 22px;
  }
`;

const CardEyebrow = styled.p`
  margin: 0 0 8px;
  color: ${theme.colors.green};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 1.8px;
  text-transform: uppercase;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 900;
`;

const CardBody = styled.p`
  margin: 14px 0 0;
  font-size: 17px;
  line-height: 1.6;
  font-weight: 600;
  color: rgba(40, 47, 51, 0.78);
`;

const CardActions = styled.div`
  margin-top: auto;
  padding-top: 24px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

/* ------------------------------------------------------------ Video assets */
const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  margin-top: 24px;
  padding-top: 56.25%;
  border-radius: 14px;
  overflow: hidden;
  background: ${theme.colors.black};

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

/* --------------------------------------------------------------- Help CTA */
const HelpSectionOuter = styled.section`
  padding: 24px 24px 96px;

  @media ${MOBILE_QUERY} {
    padding: 8px 20px 72px;
  }
`;

const HelpBand = styled.div`
  max-width: 1128px;
  margin: 0 auto;
  padding: 48px 56px;
  box-sizing: border-box;
  border-radius: 20px;
  background: ${theme.colors.blueDark};

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;

  @media ${MOBILE_QUERY} {
    flex-direction: column;
    align-items: flex-start;
    padding: 36px 28px;
  }
`;

const HelpContent = styled.div`
  flex: 1;
`;

const HelpTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.white};
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: -0.03em;
`;

const HelpText = styled.p`
  margin: 14px 0 0;
  max-width: 620px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 18px;
  line-height: 1.55;
  font-weight: 600;
`;
